import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Paper,
        Popover,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../helpers/axios';
import AssignRoleModal from './AssignRoleModal';
import PaginationTable from '../status/PaginationTable';
import { ROLE_OPTIONS, ROLE_LABEL_BY_VALUE } from './roleOptions';
import { useInstituteOptions } from '../FibeCommissionBilling/hooks/useInstituteOptions';

const TOP_TIER_ROLES = ['admin', 'ceo', 'cxo'];
const MANAGER_ROLES = ['crm', 'collection_manager', 'zonal_processing', 'zonal_collection', 'fund_manager'];

function roleChipColor(role) {
    if (TOP_TIER_ROLES.includes(role)) return 'primary';
    if (MANAGER_ROLES.includes(role)) return 'secondary';
    return 'default';
}

const ROWS_PER_PAGE_OPTIONS = [15, 50, 100, 500];

function UserRolesTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function UserRolesTab() {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [role, setRole] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalUser, setModalUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { institutes: allInstitutes } = useInstituteOptions();
    const instituteNameById = new Map(allInstitutes.map((i) => [i.id, i.brandName]));
    const [instituteAnchorEl, setInstituteAnchorEl] = useState(null);
    const [instituteList, setInstituteList] = useState([]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(handle);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, role]);

    const loadUsers = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/admin-settings/users', {
                params: {
                    page: currentPage,
                    limit: rowsPerPage,
                    search: debouncedSearch || undefined,
                    role: role || undefined,
                },
            })
            .then((response) => {
                setUsers(response.data.data.users);
                setTotal(response.data.data.total);
            })
            .catch(() => setErrorMessage('Could not load users.'))
            .finally(() => setIsLoading(false));
    }, [currentPage, rowsPerPage, debouncedSearch, role]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleEditUser = (user) => {
        setModalUser(user || null);
        setModalOpen(true);
    };

    const handleAssignSuccess = () => {
        setSnackbar({ open: true, message: 'Role assigned successfully', severity: 'success' });
        loadUsers();
    };

    const handleRowsPerPageChange = (size) => {
        setRowsPerPage(size);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearch('');
        setRole('');
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />
                    <Select
                        size="small"
                        displayEmpty
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        sx={{ minWidth: 200 }}
                        MenuProps={{
                            variant: 'menu',
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: { style: { maxHeight: 250 } },
                        }}
                    >
                        <MenuItem value="">All Roles</MenuItem>
                        {ROLE_OPTIONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </Select>
                    <button
                        onClick={handleClearFilters}
                        aria-label="Clear filters"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            height: '35px',
                            padding: '0 16px',
                            backgroundColor: '#D22129',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontFamily: 'Inter-Medium',
                            fontSize: '14px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            alignSelf: 'center',
                        }}
                    >
                        <RefreshIcon />
                    </button>
                </Box>
                <Button variant="contained" onClick={() => handleEditUser()} sx={{ color: '#fff' }}>Assign Role</Button>
            </Box>

            {errorMessage && <Box sx={{ color: 'error.main', marginBottom: '12px' }}>{errorMessage}</Box>}

            <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Scope Type</TableCell>
                                <TableCell>Zone/Team</TableCell>
                                <TableCell>Assigned Institutes</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <UserRolesTableSkeleton />
                        ) : (
                            <TableBody>
                                {users.map((user) => {
                                    const institutes = user.assignedInstitutes || [];
                                    const zoneOrTeam = user.zoneName ? `Zone: ${user.zoneName}` : user.teamName ? `Team: ${user.teamName}` : '—';
                                    return (
                                        <TableRow key={user.id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Avatar sx={{ width: 32, height: 32 }}>{user.name?.[0]?.toUpperCase()}</Avatar>
                                                    <Box>
                                                        <Box sx={{ fontWeight: 500 }}>{user.name}</Box>
                                                        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{user.email}</Box>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={roleChipColor(user.role)}
                                                    label={ROLE_LABEL_BY_VALUE[user.role] || user.role}
                                                    sx={roleChipColor(user.role) === 'primary' ? { color: '#fff' } : undefined}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={user.scopeType || '—'} />
                                            </TableCell>
                                            <TableCell>{zoneOrTeam}</TableCell>
                                            <TableCell>
                                                {institutes.length === 0 ? '—' : (
                                                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                        {institutes.slice(0, 2).map((instId) => (
                                                            <Chip key={instId} size="small" label={instituteNameById.get(instId) || instId} />
                                                        ))}
                                                        {institutes.length > 2 && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label={`+${institutes.length - 2} more`}
                                                                onClick={(e) => {
                                                                    setInstituteAnchorEl(e.currentTarget);
                                                                    setInstituteList(institutes.map((instId) => instituteNameById.get(instId) || instId));
                                                                }}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleEditUser(user)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            </Paper>

            {total > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '16px', marginBottom: '10px', flexWrap: 'wrap', gap: '4px' }}>
                    <Box sx={{ fontWeight: 'bold', fontFamily: 'Inter-Medium', fontSize: '14px', marginRight: '10px' }}>
                        Showing {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, total)} records of {total} |
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, marginRight: '8px' }}>Rows Per Pages :</Typography>
                        {ROWS_PER_PAGE_OPTIONS.map((size) => (
                            <Box
                                key={size}
                                component="span"
                                onClick={() => handleRowsPerPageChange(size)}
                                sx={{
                                    cursor: 'pointer',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    color: rowsPerPage === size ? '#D32028' : 'black',
                                    backgroundColor: rowsPerPage === size ? '#FFF2F3' : 'transparent',
                                }}
                            >
                                {size}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <PaginationTable
                startPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={total}
                paginate={setCurrentPage}
            />

            <AssignRoleModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                user={modalUser}
                onSuccess={handleAssignSuccess}
            />

            <Popover
                open={Boolean(instituteAnchorEl)}
                anchorEl={instituteAnchorEl}
                onClose={() => setInstituteAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: 280 }}>
                    {instituteList.map((name) => (
                        <Chip key={name} size="small" label={name} sx={{ justifyContent: 'flex-start' }} />
                    ))}
                </Box>
            </Popover>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
